import React, { useEffect, useState } from 'react';
import { AlertTriangle, Target, Clock, Building2, Users, RefreshCw, Loader2, ArrowRight, Flame, MessageSquareReply, FileSignature, DollarSign, FileText, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRMCommandCenter = ({ embedded = false, onNavigateTab }) => {
  const [data, setData] = useState(null);
  const [phase2, setPhase2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetchWithAuth(`${API_URL}/api/crm/command-center`),
      fetchWithAuth(`${API_URL}/api/crm/phase2/command-center-summary`),
    ]).then(([d, p2]) => { setData(d); setPhase2(p2); }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/crm/sync-signal-feed`, { method: 'POST' });
      toast.success(`Synced: ${res.companies_created} new, ${res.companies_refreshed} refreshed companies · ${res.contacts_created} new contacts`);
      load();
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <IntelligenceLayout title="CRM Command Center" embedded={embedded}>
      <div className="p-6 space-y-6" data-testid="crm-command-center">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">What needs your attention</h1>
            <p className="text-sm text-gray-500 mt-1">Native Omnia CRM — client &amp; business-development command center</p>
          </div>
          <button onClick={handleSync} disabled={syncing} data-testid="sync-signal-feed-btn"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90 disabled:opacity-50 transition-colors">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync from Signal Feed
          </button>
        </div>

        {loading || !data ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Overdue Follow-ups" value={data.overdue_count} subtext="Past due next action" icon={AlertTriangle} />
              <MetricCard label="Active Opportunities" value={data.active_opportunities} subtext="Discovery → Job Order" icon={Target} />
              <MetricCard label="Needs Attention" value={data.needs_attention_count} subtext="Stalled 14+ days" icon={Clock} />
              <MetricCard label="Companies Tracked" value={data.total_companies} subtext={`${data.total_contacts} contacts`} icon={Building2} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <CommandList
                testId="overdue-list" title="Overdue — Who to contact next" icon={AlertTriangle} accent="red"
                items={data.overdue_companies} empty="Nothing overdue — great job."
                renderMeta={(c) => `Due ${c.next_action_due_date || '—'} · ${c.next_action || 'No action set'}`}
              />
              <CommandList
                testId="attention-list" title="Needs Attention — Stuck deals" icon={Clock} accent="amber"
                items={data.needs_attention_list} empty="No stalled opportunities right now."
                renderMeta={(c) => `${c.pipeline_stage} · last activity ${c.last_activity_at ? c.last_activity_at.slice(0, 10) : 'never'}`}
              />
              <CommandList
                testId="opportunities-list" title="Opportunities — Closest to revenue" icon={Target} accent="green"
                items={data.active_opportunity_list} empty="No open opportunities past Discovery yet."
                renderMeta={(c) => c.pipeline_stage}
              />
              <CommandList
                testId="high-priority-list" title="High-Priority Prospects" icon={Flame} accent="orange"
                items={data.high_priority_prospects} empty="No scored high-priority prospects right now."
                totalCount={data.high_priority_prospect_count} onViewAll={() => onNavigateTab && onNavigateTab('companies', { scored: 'true' })}
                renderMeta={(c) => `Score ${c.signal_score ?? '—'} (${c.signal_priority || 'unranked'}) · ${c.pipeline_stage} · ${c.next_action_due_date && c.next_action_due_date <= new Date().toISOString().slice(0, 10) ? 'OVERDUE' : c.next_action || 'No action set'}`}
              />
              <CommandList
                testId="responses-list" title="Recent Responses" icon={MessageSquareReply} accent="blue"
                items={data.recent_responses} empty="No responses logged yet."
                totalCount={data.recent_response_count} onViewAll={() => onNavigateTab && onNavigateTab('companies', { responded: 'true' })}
                renderMeta={(c) => `${c.primary_contact_name || 'No contact on file'} · responded ${c.last_response_at ? c.last_response_at.slice(0, 10) : '—'}${c.last_response_status === 'interested' ? ' · INTERESTED' : ''}${!c.next_action ? ' · UNANSWERED' : ''}`}
              />
              <CommandList
                testId="active-clients-list" title="Active Clients" icon={Building2} accent="teal"
                items={data.active_clients} empty="No active clients yet."
                totalCount={data.active_client_count} onViewAll={() => onNavigateTab && onNavigateTab('companies', { stage: 'Active Client' })}
                renderMeta={(c) => `Job orders: ${c.active_job_order_count ?? '—'} · Revenue opp: ${c.open_revenue_opportunity ?? '— (Phase 2)'} · last activity ${c.last_activity_at ? c.last_activity_at.slice(0, 10) : 'never'}`}
              />
            </div>

            {phase2 && (
              <>
                <div className="flex items-center justify-between pt-2">
                  <h2 className="text-sm font-bold text-[#0A1F44] uppercase tracking-wide">Phase 2 — Commercial Ops</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <MetricCard label="Estimated Pipeline Revenue" value={`$${phase2.estimated_pipeline_revenue.toLocaleString()}`} subtext="Open job orders, est. fee" icon={DollarSign} />
                  <MetricCard label="Actual Invoiced Revenue" value={`$${phase2.actual_invoiced_revenue.toLocaleString()}`} subtext="All revenue records" icon={FileText} />
                  <MetricCard label="Collected Revenue" value={`$${phase2.collected_revenue.toLocaleString()}`} subtext="Payments received" icon={CheckCircle2} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <CommandList
                    testId="agreements-awaiting-list" title="Agreements Awaiting Action" icon={FileSignature} accent="amber"
                    items={phase2.agreements_awaiting} empty="No agreements awaiting signature."
                    totalCount={phase2.agreements_awaiting_count} onViewAll={() => onNavigateTab && onNavigateTab('agreements', { awaiting: 'true' })}
                    renderTitle={(a) => a.company_name || 'Unknown company'}
                    renderMeta={(a) => `${a.status} · ${a.fee_percentage}% fee · sent ${a.date_sent || '—'}`}
                  />
                  <CommandList
                    testId="job-orders-overdue-list" title="Job Orders — Overdue Next Action" icon={AlertTriangle} accent="red"
                    items={phase2.overdue_job_orders} empty="No overdue job order actions."
                    totalCount={phase2.overdue_job_orders_count} onViewAll={() => onNavigateTab && onNavigateTab('job-orders', { overdue: 'true' })}
                    renderTitle={(j) => j.job_title}
                    renderMeta={(j) => `${j.company_name || ''} · due ${j.next_action_due_date} · ${j.next_action || 'No action set'}`}
                  />
                  <CommandList
                    testId="ready-to-invoice-list" title="Ready to Invoice" icon={DollarSign} accent="green"
                    items={phase2.ready_to_invoice} empty="Nothing ready to invoice yet."
                    totalCount={phase2.ready_to_invoice_count} onViewAll={() => onNavigateTab && onNavigateTab('revenue', { ready: 'true' })}
                    renderTitle={(r) => r.candidate_name || r.job_title || 'Placement'}
                    renderMeta={(r) => `${r.company_name || ''} · ${r.job_title || ''} · fee $${(r.actual_fee || 0).toLocaleString()}`}
                  />
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <button onClick={() => onNavigateTab && onNavigateTab('job-orders', { no_link: 'true' })} data-testid="no-manatal-link-chip" className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#D4AF37]/40">
                    {phase2.no_manatal_link_count} job order{phase2.no_manatal_link_count === 1 ? '' : 's'} with no linked Manatal job
                  </button>
                  <button onClick={() => onNavigateTab && onNavigateTab('revenue', { outstanding: 'true' })} data-testid="outstanding-invoices-chip" className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg hover:border-[#D4AF37]/40">
                    {phase2.outstanding_invoices_count} invoice{phase2.outstanding_invoices_count === 1 ? '' : 's'} outstanding
                  </button>
                  <button onClick={() => onNavigateTab && onNavigateTab('revenue', { overdue: 'true' })} data-testid="overdue-invoices-chip" className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-red-600 hover:border-red-300">
                    {phase2.overdue_invoices_count} invoice{phase2.overdue_invoices_count === 1 ? '' : 's'} overdue
                  </button>
                </div>
              </>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="stage-funnel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-[#0A1F44]">Pipeline at a glance</h3>
                <button onClick={() => onNavigateTab && onNavigateTab('pipeline')} className="text-xs font-medium text-[#0A1F44] flex items-center gap-1 hover:underline">
                  View full pipeline <ArrowRight className="h-3 w-3" />
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(data.stage_counts).map(([stage, count]) => (
                  <div key={stage} className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-center min-w-[100px]" data-testid={`stage-count-${stage.replace(/\s+/g, '-').toLowerCase()}`}>
                    <p className="text-lg font-bold text-[#0A1F44]">{count}</p>
                    <p className="text-[11px] text-gray-500">{stage}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </IntelligenceLayout>
  );
};

const ACCENTS = {
  red: { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-500' },
  amber: { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-500' },
  green: { border: 'border-green-200', bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-500' },
  orange: { border: 'border-orange-200', bg: 'bg-orange-50', text: 'text-orange-700', icon: 'text-orange-500' },
  blue: { border: 'border-blue-200', bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-500' },
  teal: { border: 'border-teal-200', bg: 'bg-teal-50', text: 'text-teal-700', icon: 'text-teal-500' },
};

const CommandList = ({ testId, title, icon: Icon, accent, items, empty, renderMeta, renderTitle, totalCount, onViewAll }) => {
  const c = ACCENTS[accent];
  return (
    <div className={`bg-white rounded-xl border ${c.border} shadow-sm overflow-hidden`} data-testid={testId}>
      <div className={`${c.bg} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${c.icon}`} />
          <h3 className={`text-sm font-semibold ${c.text}`}>{title}</h3>
        </div>
        {onViewAll && (totalCount || 0) > 0 && (
          <button onClick={onViewAll} data-testid={`${testId}-view-all`} className={`text-[11px] font-medium ${c.text} hover:underline`}>
            View all {totalCount}
          </button>
        )}
      </div>
      <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
        {(!items || items.length === 0) ? (
          <p className="text-sm text-gray-400 p-4">{empty}</p>
        ) : items.map((c2) => (
          <div key={c2.id} className="px-4 py-2.5" data-testid={`${testId}-item-${c2.id}`}>
            <p className="text-sm font-medium text-[#0A1F44] truncate">{renderTitle ? renderTitle(c2) : c2.name}</p>
            <p className="text-xs text-gray-500 truncate">{renderMeta(c2)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CRMCommandCenter;
